<details class="viewer-inspector__section" open>
  <summary>Navigation</summary>
  <div class="viewer-panel__state">
    Left drag looks around · right drag orbits around the point you press · middle drag pans · wheel zooms ·
    double-click turns to a point · W A S D Q E walk.
  </div>
  <div class="viewer-panel__actions">
    <input type="button" class="demo-button viewer-panel__button" value="Home" (tap)="viewer.moveCameraHome()" />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Fit all"
      title="Zoom to the whole scene without turning the camera"
      (tap)="viewer.fitAll()"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Zoom to selection (F)"
      [disabled]="viewer.selectedSceneNodeId === null"
      (tap)="viewer.focusSelectedSceneNode()"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      [value]="viewer.parallelProjection ? 'Perspective (P)' : 'Parallel projection (P)'"
      title="Parallel projection for plans and elevations"
      (tap)="viewer.toggleParallelProjection()"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      [value]="viewer.isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
      (tap)="viewer.toggleFullscreen()"
    />
  </div>
  <div class="viewer-panel__actions" role="group" aria-label="Standard views">
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Front (1)"
      (tap)="viewer.setCameraView('front')"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Back (2)"
      (tap)="viewer.setCameraView('back')"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Left (3)"
      (tap)="viewer.setCameraView('left')"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Right (4)"
      (tap)="viewer.setCameraView('right')"
    />
    <input type="button" class="demo-button viewer-panel__button" value="Top (5)" (tap)="viewer.setCameraView('top')" />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Bottom (6)"
      (tap)="viewer.setCameraView('bottom')"
    />
    <input type="button" class="demo-button viewer-panel__button" value="Iso (0)" (tap)="viewer.setCameraView('iso')" />
  </div>
  <div class="viewer-panel__actions">
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Use current camera as scene start"
      title="Open this scene from now on with the current camera, unless the URL sets one"
      [disabled]="viewer.sceneSettingsBusy"
      (tap)="viewer.useCurrentCameraAsSceneStart()"
    />
    <input
      *ngIf="viewer.selectedSceneHasStartCamera"
      type="button"
      class="demo-button viewer-panel__button"
      value="Clear scene start camera"
      [disabled]="viewer.sceneSettingsBusy"
      (tap)="viewer.clearSceneStartCamera()"
    />
    <span *ngIf="viewer.sceneSettingsStatus as status" role="status">{{ status.text }}</span>
  </div>
</details>

<details class="viewer-inspector__section" open>
  <summary>Selection &amp; visibility</summary>
  <div class="viewer-panel__state">
    Selected: {{ viewer.selectedSceneNodeTitle ?? viewer.selectedSceneNodeId ?? 'nothing (click the model)' }}
  </div>
  <div class="viewer-panel__actions">
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Hide (H)"
      [disabled]="viewer.selectedSceneNodeId === null"
      (tap)="viewer.hideSelectedSceneNode()"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Isolate (I)"
      [disabled]="viewer.selectedSceneNodeId === null"
      (tap)="viewer.isolateSelectedSceneNode()"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Unhide selected"
      [disabled]="viewer.selectedSceneNodeId === null"
      (tap)="viewer.unhideSelectedSceneNode()"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Show all (U)"
      (tap)="viewer.unhideAllSceneNodes()"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      [value]="viewer.xrayEnabled ? 'X-ray off (X)' : 'X-ray (X)'"
      title="Everything but the selection turns faint"
      (tap)="viewer.toggleXray()"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Clear selection (Esc)"
      [disabled]="viewer.selectedSceneNodeId === null"
      (tap)="viewer.clearSelection()"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Remove selected root"
      [disabled]="!viewer.isSelectedSceneRootNode()"
      (tap)="viewer.removeSelectedRootNodeFromScene()"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Reload scene tree"
      [disabled]="viewer.sceneTreeLoading"
      (tap)="viewer.reloadSceneTree()"
    />
  </div>
  <div class="viewer-panel__state" title="Hide, Isolate and Show all apply to this browser only">
    Visibility session: {{ viewer.visibilitySessionId.slice(0, 8) }} (this browser)
  </div>
  <div class="viewer-panel__actions">
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="New visibility session"
      title="Start over with the scene's own visibility; what this browser hid shows again"
      (tap)="viewer.regenerateVisibilitySession()"
    />
  </div>
</details>

<details class="viewer-inspector__section" open>
  <summary>Section</summary>
  <div class="viewer-panel__controls">
    <label class="viewer-panel__control-label" for="sectionMode">Mode</label>
    <select
      id="sectionMode"
      class="viewer-panel__select"
      [value]="viewer.section.mode"
      (change)="viewer.setSectionMode($any($event.target).value)"
    >
      <option value="off">Off</option>
      <option value="plane">Plane</option>
      <option value="box">Box (around the selection)</option>
    </select>

    <ng-container *ngIf="viewer.section.mode === 'plane'">
      <div *ngIf="viewer.section.customNormal !== null" class="viewer-panel__state">Along a clicked face.</div>
      <label class="viewer-panel__control-label" for="sectionAxis">Across</label>
      <select
        id="sectionAxis"
        class="viewer-panel__select"
        [value]="viewer.section.axis"
        (change)="viewer.setSectionAxis($any($event.target).value)"
      >
        <option value="x">X</option>
        <option value="y">Y (height)</option>
        <option value="z">Z</option>
      </select>

      <label class="viewer-panel__control-label" for="sectionPosition"
        >{{ viewer.section.customNormal === null ? viewer.section.axis.toUpperCase() : 'Along the face' }} =
        {{ viewer.section.position | number: '1.2-2' }} m</label
      >
      <input
        id="sectionPosition"
        class="viewer-panel__control-input"
        type="range"
        [min]="viewer.section.range?.min ?? 0"
        [max]="viewer.section.range?.max ?? 0"
        [step]="viewer.sectionStep"
        [value]="viewer.section.position"
        (input)="viewer.onSectionPositionChanged($event)"
      />
    </ng-container>
    <ng-container *ngIf="viewer.section.mode === 'box' && viewer.section.box as box">
      <ng-container *ngFor="let axis of sectionAxes">
        <label class="viewer-panel__control-label" [attr.for]="'sectionBoxMin' + axis"
          >{{ axis.toUpperCase() }} from {{ box.min[axisIndex[axis]] | number: '1.2-2' }} m</label
        >
        <input
          [id]="'sectionBoxMin' + axis"
          class="viewer-panel__control-input"
          type="range"
          [min]="viewer.sectionBoxRange(axis).min"
          [max]="viewer.sectionBoxRange(axis).max"
          [step]="viewer.sectionBoxRange(axis).step"
          [value]="box.min[axisIndex[axis]]"
          (input)="viewer.onSectionBoxFaceChanged(axis, 'min', $event)"
        />
        <label class="viewer-panel__control-label" [attr.for]="'sectionBoxMax' + axis"
          >{{ axis.toUpperCase() }} to {{ box.max[axisIndex[axis]] | number: '1.2-2' }} m</label
        >
        <input
          [id]="'sectionBoxMax' + axis"
          class="viewer-panel__control-input"
          type="range"
          [min]="viewer.sectionBoxRange(axis).min"
          [max]="viewer.sectionBoxRange(axis).max"
          [step]="viewer.sectionBoxRange(axis).step"
          [value]="box.max[axisIndex[axis]]"
          (input)="viewer.onSectionBoxFaceChanged(axis, 'max', $event)"
        />
      </ng-container>
    </ng-container>
  </div>
  <div class="viewer-panel__actions">
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Cut at a clicked point"
      title="The next click on the model places the plane there"
      [disabled]="viewer.sectionPickArmed !== false"
      (tap)="viewer.armSectionPick('point')"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      value="Cut along a clicked face"
      title="The next click on a face places the plane along it, opening the face up"
      [disabled]="viewer.sectionPickArmed !== false"
      (tap)="viewer.armSectionPick('face')"
    />
    <input
      *ngIf="viewer.section.mode !== 'off'"
      type="button"
      class="demo-button viewer-panel__button"
      [value]="
        viewer.sectionDragArmed
          ? 'Stop dragging'
          : viewer.section.mode === 'box'
          ? 'Resize the box in the view'
          : 'Drag in the view'
      "
      title="While on, dragging on the view moves the plane"
      (tap)="viewer.toggleSectionDrag()"
    />
    <input
      *ngIf="viewer.section.mode === 'plane'"
      type="button"
      class="demo-button viewer-panel__button"
      value="Flip side"
      (tap)="viewer.flipSection()"
    />
    <input
      type="button"
      class="demo-button viewer-panel__button"
      [value]="viewer.section.mode === 'box' ? 'Fit box to selection' : 'Box around selection'"
      title="Keep only what is inside a box around the selection (the whole scene without one); nothing outside is downloaded"
      (tap)="viewer.sectionBoxAroundSelection()"
    />
  </div>
</details>

<details class="viewer-inspector__section">
  <summary>Appearance</summary>
  <div class="viewer-panel__controls">
    <label class="viewer-panel__control-label" for="sceneOpacity"
      >Global opacity: {{ viewer.sceneOpacity | number: '1.2-2' }}</label
    >
    <input
      id="sceneOpacity"
      class="viewer-panel__control-input"
      type="range"
      min="0.05"
      max="1"
      step="0.05"
      [value]="viewer.sceneOpacity"
      (change)="viewer.onSceneOpacityChanged($event)"
    />
  </div>
</details>

<details class="viewer-inspector__section" open>
  <summary>Measurement</summary>
  <div class="viewer-panel__measurement">
    <div class="viewer-panel__measurement-header">
      <div>
        <div class="viewer-panel__subtitle">Measure in the scene</div>
        <div class="viewer-panel__state">
          Click points on the model; values are in world meters. Off the model, points stay on the first point's level.
        </div>
      </div>
      <label class="viewer-panel__measurement-toggle" for="measurementEnabled">
        <span>{{ viewer.measurements.enabled ? 'Active' : 'Inactive' }}</span>
        <input
          id="measurementEnabled"
          class="viewer-panel__control-checkbox"
          type="checkbox"
          [checked]="viewer.measurements.enabled"
          (change)="viewer.onMeasurementEnabledChanged($event)"
        />
      </label>
    </div>

    <div class="viewer-panel__controls">
      <label class="viewer-panel__control-label" for="measurementMode">Mode</label>
      <select
        id="measurementMode"
        class="viewer-panel__select"
        [value]="viewer.measurements.tool.mode"
        (change)="viewer.onMeasurementModeChanged($event)"
      >
        <option value="distance">Distance</option>
        <option value="angle">Angle</option>
        <option value="polyline">Polyline</option>
        <option value="area">Area</option>
        <option value="point">Point (coordinates)</option>
      </select>

      <label class="viewer-panel__control-label" for="measurementSnapMode">Snap</label>
      <select
        id="measurementSnapMode"
        class="viewer-panel__select"
        [value]="viewer.measurements.tool.snapMode"
        (change)="viewer.onMeasurementSnapModeChanged($event)"
      >
        <option value="auto">Surface</option>
        <option value="selected-node">Selected node</option>
        <option value="plane">First point level</option>
      </select>

      <label class="viewer-panel__control-label" for="measurementUnit">Unit</label>
      <select
        id="measurementUnit"
        class="viewer-panel__select"
        [value]="viewer.measurements.tool.unit"
        (change)="viewer.onMeasurementUnitChanged($event)"
      >
        <option value="m">m</option>
        <option value="cm">cm</option>
        <option value="mm">mm</option>
      </select>
    </div>

    <div *ngIf="viewer.measurements.enabled" class="viewer-panel__state">{{ viewer.measurements.hint }}</div>
    <label class="viewer-panel__measurement-toggle" for="measurementSnapEdges">
      <input
        id="measurementSnapEdges"
        class="viewer-panel__control-checkbox"
        type="checkbox"
        [checked]="viewer.measurementSnapToEdges"
        (change)="viewer.measurementSnapToEdges = $any($event.target).checked"
      />
      <span>Snap to corners, edge middles and edges</span>
    </label>
    <div class="viewer-panel__state">Value: {{ viewer.measurements.tool.label }}</div>
    <div *ngIf="viewer.measurements.deltasLabel as deltas" class="viewer-panel__state">{{ deltas }}</div>
    <div class="viewer-panel__state">Points: {{ viewer.measurements.pointsLabel }}</div>
    <div class="viewer-panel__state">Last point: {{ viewer.measurements.lastPointLabel }}</div>
    <div class="viewer-panel__actions">
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Clear Points"
        [disabled]="viewer.measurements.tool.points.length === 0"
        (tap)="viewer.clearMeasurement()"
      />
      <input
        *ngIf="viewer.measurements.tool.targetPoints === 0"
        type="button"
        class="demo-button viewer-panel__button"
        value="Save"
        [disabled]="!viewer.measurements.tool.canFinish"
        (tap)="viewer.saveMeasurement()"
      />
    </div>

    <div class="viewer-panel__subtitle">Model scale</div>
    <div class="viewer-panel__state">
      {{
        viewer.measurementScale === 1
          ? 'Model units are meters.'
          : '1 model unit = ' + (viewer.measurementScale | number: '1.0-4') + ' m (calibrated for this scene).'
      }}
      Photogrammetry keeps the units of its source; measure a known length (a door, a car) and enter it to calibrate.
    </div>
    <div class="viewer-panel__actions">
      <input
        #calibrationLength
        class="viewer-panel__select"
        type="number"
        min="0"
        step="0.01"
        placeholder="Real length, m"
        aria-label="Real length of the measured distance in meters"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Calibrate"
        [disabled]="viewer.measurements.tool.rawValue === null"
        (tap)="viewer.calibrateMeasurementScale(calibrationLength.value)"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Reset scale"
        [disabled]="viewer.measurementScale === 1"
        (tap)="viewer.resetMeasurementScale()"
      />
    </div>

    <div class="viewer-panel__subtitle">Saved measurements: {{ viewer.measurements.history.length }}</div>
    <select
      class="viewer-panel__select"
      [value]="viewer.measurements.selectedId ?? ''"
      (change)="viewer.onSelectedMeasurementChanged($event)"
    >
      <option value="">Show a saved measurement</option>
      <option *ngFor="let entry of viewer.measurements.history" [value]="entry.id">
        {{ viewer.measurements.formatEntry(entry) }}
      </option>
    </select>
    <div class="viewer-panel__actions">
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Delete Saved"
        [disabled]="viewer.measurements.selectedId === null"
        (tap)="viewer.deleteSelectedMeasurement()"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Clear History"
        [disabled]="viewer.measurements.history.length === 0"
        (tap)="viewer.clearMeasurementHistory()"
      />
    </div>
  </div>
</details>

<details class="viewer-inspector__section">
  <summary>Annotations</summary>
  <div class="viewer-panel__state">
    Markers pin names to points of the model and show them in the view; viewpoints live in the Viewpoints tab. Export or
    import both as JSON below.
  </div>
  <div class="viewer-panel__markers">
    <div class="viewer-panel__subtitle">Markers</div>
    <div class="viewer-panel__state">Total: {{ viewer.markers.length }}</div>

    <label class="viewer-panel__control-label" for="markerPlacementEnabled">Marker placement mode</label>
    <input
      id="markerPlacementEnabled"
      class="viewer-panel__control-checkbox"
      type="checkbox"
      [checked]="viewer.markerPlacementEnabled"
      (change)="viewer.onMarkerPlacementEnabledChanged($event)"
    />

    <label class="viewer-panel__control-label" for="markersVisible">Show markers</label>
    <input
      id="markersVisible"
      class="viewer-panel__control-checkbox"
      type="checkbox"
      [checked]="viewer.markersVisible"
      (change)="viewer.onMarkersVisibleChanged($event)"
    />

    <select
      class="viewer-panel__select"
      [value]="viewer.selectedMarkerId ?? ''"
      (change)="viewer.onSelectedMarkerChanged($event)"
    >
      <option value="">Select marker</option>
      <option *ngFor="let marker of viewer.markers" [value]="marker.id">{{ marker.name }}</option>
    </select>

    <label class="viewer-panel__control-label" for="markerNameDraft">Marker label</label>
    <input
      id="markerNameDraft"
      class="viewer-panel__select"
      type="text"
      [value]="viewer.markerNameDraft"
      (input)="viewer.onMarkerNameDraftChanged($event)"
      placeholder="Marker name"
    />

    <div class="viewer-panel__actions">
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Focus Marker"
        [disabled]="viewer.selectedMarkerId === null"
        (tap)="viewer.focusSelectedMarker()"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Rename Marker"
        [disabled]="viewer.selectedMarkerId === null"
        (tap)="viewer.renameSelectedMarker()"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Delete Marker"
        [disabled]="viewer.selectedMarkerId === null"
        (tap)="viewer.deleteSelectedMarker()"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Clear Markers"
        (tap)="viewer.clearMarkers()"
      />
    </div>
  </div>
  <div class="viewer-panel__transfer">
    <div class="viewer-panel__subtitle">Viewer Data Transfer</div>
    <div class="viewer-panel__state">Export/import viewpoints and markers as JSON.</div>
    <label class="viewer-panel__control-label" for="viewerImportMode">Import mode</label>
    <select
      id="viewerImportMode"
      class="viewer-panel__select"
      [value]="viewer.viewerDataImportMode"
      (change)="viewer.onViewerDataImportModeChanged($event)"
    >
      <option value="replace">Replace existing</option>
      <option value="merge">Merge by id</option>
    </select>
    <textarea
      class="viewer-panel__transfer-textarea"
      [value]="viewer.viewerDataTransferText"
      (input)="viewer.onViewerDataTransferTextChanged($event)"
      placeholder="Paste or generate viewer JSON here"
    ></textarea>
    <label class="viewer-panel__control-label" for="viewerDataFileInput">Upload JSON file</label>
    <input
      id="viewerDataFileInput"
      type="file"
      accept="application/json,.json"
      (change)="viewer.onViewerDataFileChanged($event)"
    />
    <div class="viewer-panel__actions">
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Export JSON"
        (tap)="viewer.exportViewerData()"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Download JSON"
        (tap)="viewer.downloadViewerDataFile()"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Copy JSON"
        (tap)="viewer.copyViewerDataToClipboard()"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Import JSON"
        (tap)="viewer.importViewerData()"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Clear JSON"
        (tap)="viewer.clearViewerDataTransferText()"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Save to Backend"
        (tap)="viewer.saveViewerStateToBackend()"
      />
      <input
        type="button"
        class="demo-button viewer-panel__button"
        value="Load from Backend"
        (tap)="viewer.loadViewerStateFromBackend()"
      />
    </div>
  </div>
</details>
